<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import InputNumber from 'primevue/inputnumber';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import MeasurementInput from '@/common/components/MeasurementInput.vue';
import { useFieldUnit } from 'shared/common/composables/fieldUnit';
import { unitSymbol, toDisplayUnit, type MeasurementUnit } from 'shared/common/domain/units';
import { useSelectedPackageStorageStore } from '../stores/selectedPackageStorageStore';
import type { StoragePlace } from '../stores/selectedPackageStorageStore';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { useAuthStore } from '@/auth/store/authStore';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { computeStoragePlaceDisplayText } from 'shared/storageAreas/domain/computePlaceDisplayText';
import StoragePlaceDisplayTextFormulaDialog from './StoragePlaceDisplayTextFormulaDialog.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import {
  PlaceAccessDirection,
  allPlaceAccessDirections,
  hasAccessDirection,
} from 'shared/storageAreas/domain/placeAccessDirection';

const selectedPackageStorageStore = useSelectedPackageStorageStore();
const {
  defaultPlaceLength,
  defaultPlaceLengthChanged,
  defaultPlaceWidth,
  defaultPlaceWidthChanged,
  defaultMaxStackCount,
  defaultMaxStackCountChanged,
  defaultMaxWeight,
  defaultMaxWeightChanged,
  defaultMaxHeight,
  defaultMaxHeightChanged,
  places,
  selectedPlaceIds,
  selectedPlaces,
  editMultiplePlaces,
  gridColumns,
  gridRows,
  horizontalGap,
  verticalGap,
  areaZIndex,
} = storeToRefs(selectedPackageStorageStore);

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const selectedStorageAreaStore = useSelectedStorageAreaStore();
const settingStore = useSettingStore();
const authStore = useAuthStore();

const showPlaceDisplayTextFormulaDialog = ref(false);

function placeDisplayTextPreview(place: StoragePlace): string {
  const area = selectedStorageAreaStore.currentStorageArea;
  return computeStoragePlaceDisplayText(
    settingStore.setting.sharedClient.storageArea.placeDisplayTextFormula,
    place,
    {
      id: area?.id ?? '',
      name: selectedStorageAreaStore.name ?? area?.name ?? '',
      status: selectedStorageAreaStore.status ?? area?.status ?? StorageAreaStatus.Empty,
    },
  );
}

// Store distance fields are non-nullable numbers; wrap them so the measurement
// input (which can emit null when cleared) coerces null back to 0.
function numberModel(target: Ref<number>) {
  return computed<number | null>({
    get: () => target.value,
    set: (value) => {
      target.value = value ?? 0;
    },
  });
}

const defaultLength = numberModel(defaultPlaceLength);
const defaultWidth = numberModel(defaultPlaceWidth);
const hGap = numberModel(horizontalGap);
const vGap = numberModel(verticalGap);

// Units for the table columns follow the matching per-place editor fields.
const placeXUnit = useFieldUnit('package-place-x');
const placeYUnit = useFieldUnit('package-place-y');
const placeWidthUnit = useFieldUnit('package-place-width');
const placeLengthUnit = useFieldUnit('package-place-length');

type PlaceColumnKind = 'plain' | 'unit' | 'displayText' | 'access' | 'disabled';

interface PlaceColumn extends ColumnField {
  kind: PlaceColumnKind;
  /** Table header when it differs from the column selector label. */
  tableHeader?: string;
  unitLabel?: string;
  unit?: MeasurementUnit;
}

const placeColumns = computed<PlaceColumn[]>(() => [
  { field: 'index', header: '#', kind: 'plain' },
  { field: 'column', header: 'Col', kind: 'plain' },
  { field: 'row', header: 'Row', kind: 'plain' },
  { field: 'x', header: 'X', kind: 'unit', unitLabel: 'X', unit: placeXUnit.value },
  { field: 'y', header: 'Y', kind: 'unit', unitLabel: 'Y', unit: placeYUnit.value },
  { field: 'width', header: 'Width', kind: 'unit', unitLabel: 'W', unit: placeWidthUnit.value },
  { field: 'length', header: 'Length', kind: 'unit', unitLabel: 'L', unit: placeLengthUnit.value },
  { field: 'zIndex', header: 'Z-index', kind: 'plain', tableHeader: 'Z' },
  { field: 'accessibilityScoreDecrementPerBlocker', header: 'Score Δ/Blocker', kind: 'plain' },
  { field: 'displayText', header: translations.value.systemSettings.page.storagearea.placeDisplayText, kind: 'displayText' },
  { field: 'access', header: 'Access', kind: 'access' },
  { field: 'disabled', header: 'Disabled', kind: 'disabled' },
]);

const {
  orderedColumns: orderedPlaceColumns,
  selectedColumns: selectedPlaceColumns,
  setSelectedColumns: setSelectedPlaceColumns,
  setOrder: setPlaceColumnsOrder,
  reset: resetPlaceColumns,
} = useColumnSelector<PlaceColumn>({
  storageKey: 'package-storage-places-columns',
  columns: placeColumns,
});

const directionLabels: { direction: PlaceAccessDirection; label: string }[] = [
  { direction: PlaceAccessDirection.Front, label: 'Front' },
  { direction: PlaceAccessDirection.Right, label: 'Right' },
  { direction: PlaceAccessDirection.Back, label: 'Back' },
  { direction: PlaceAccessDirection.Left, label: 'Left' },
];

const tableSelection = computed<StoragePlace[]>({
  get: () => selectedPlaces.value,
  set: (rows) => selectedPackageStorageStore.setSelection(rows.map((r) => r.id)),
});

const singlePlace = computed<StoragePlace | null>(() =>
  selectedPlaces.value.length === 1 ? selectedPlaces.value[0]! : null,
);

function placeField<K extends keyof StoragePlace>(key: K) {
  return computed<StoragePlace[K] | null>({
    get: () => singlePlace.value?.[key] ?? null,
    set: (value) => {
      if (!singlePlace.value) return;
      selectedPackageStorageStore.checkpoint();
      selectedPackageStorageStore.updatePlace(singlePlace.value.id, { [key]: value } as Partial<StoragePlace>);
    },
  });
}

const placeX = placeField('x');
const placeY = placeField('y');
const placeLength = placeField('length');
const placeWidth = placeField('width');
const placeMaxStackCount = placeField('maxStackCount');
const placeMaxWeight = placeField('maxWeight');
const placeMaxHeight = placeField('maxHeight');
const placeAccessibilityScoreDecrementPerBlocker = placeField('accessibilityScoreDecrementPerBlocker');
const placeZIndex = placeField('zIndex');
const placeColumn = placeField('column');
const placeRow = placeField('row');

const singleDisabled = computed<boolean>({
  get: () => singlePlace.value?.disabled ?? false,
  set: (value) => {
    if (!singlePlace.value) return;
    selectedPackageStorageStore.checkpoint();
    selectedPackageStorageStore.updatePlace(singlePlace.value.id, { disabled: value });
  },
});

/** Returns true (all), false (none) or null (mixed) for a direction over the selection. */
function directionState(direction: PlaceAccessDirection): boolean | null {
  const sel = selectedPlaces.value;
  if (sel.length === 0) return false;
  const on = sel.filter((p) => hasAccessDirection(p.accessDirections, direction)).length;
  if (on === 0) return false;
  if (on === sel.length) return true;
  return null;
}

function toggleDirection(direction: PlaceAccessDirection) {
  if (selectedPlaceIds.value.length === 0) return;
  const enabled = directionState(direction) !== true;
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.setDirection(selectedPlaceIds.value, direction, enabled);
}

function directionSummary(place: StoragePlace): string {
  if (place.accessDirections === PlaceAccessDirection.All) return 'All';
  if (place.accessDirections === PlaceAccessDirection.None) return 'None';
  return allPlaceAccessDirections
    .filter((d) => hasAccessDirection(place.accessDirections, d))
    .map((d) => directionLabels.find((l) => l.direction === d)!.label)
    .join(' ');
}

function generate() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.generateGrid({
    columns: gridColumns.value,
    rows: gridRows.value,
    horizontalGap: horizontalGap.value,
    verticalGap: verticalGap.value,
  });
}

/** Applies a gap to the current selection when any places are selected, otherwise to all. */
function gapTarget(): string[] | undefined {
  return selectedPlaceIds.value.length > 0 ? selectedPlaceIds.value : undefined;
}

function applyHorizontalGap() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.applyGap('horizontal', horizontalGap.value, gapTarget());
}

function applyVerticalGap() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.applyGap('vertical', verticalGap.value, gapTarget());
}

function addPlace() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.addPlace();
}

function removeSelected() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.removePlaces(selectedPlaceIds.value);
}

// Batch editing state applied through explicit actions.
function setSelectedDisabled(disabled: boolean) {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.setDisabled(selectedPlaceIds.value, disabled);
}

const batchDx = ref<number | null>(0);
const batchDy = ref<number | null>(0);
const batchWidth = ref<number | null>(null);
const batchLength = ref<number | null>(null);

function applyBatchMove() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.moveBy(selectedPlaceIds.value, batchDx.value ?? 0, batchDy.value ?? 0);
}

function applyBatchResize() {
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.resize(selectedPlaceIds.value, {
    width: batchWidth.value ?? undefined,
    length: batchLength.value ?? undefined,
  });
}

const batchZIndex = ref<number | null>(null);

function applyBatchZIndex() {
  if (batchZIndex.value === null) return;
  selectedPackageStorageStore.checkpoint();
  selectedPackageStorageStore.setZIndex(selectedPlaceIds.value, batchZIndex.value);
}
</script>

<template>
  <div class="package-storage">
    <div class="package-storage-grid">
      <div class="field" :class="{ changed: defaultPlaceLengthChanged }">
        <label for="package-storage-default-place-length">Default place length</label>
        <MeasurementInput v-model:modelValue="defaultLength" unitKey="package-default-length" inputId="package-storage-default-place-length"></MeasurementInput>
      </div>

      <div class="field" :class="{ changed: defaultPlaceWidthChanged }">
        <label for="package-storage-default-place-width">Default place width</label>
        <MeasurementInput v-model:modelValue="defaultWidth" unitKey="package-default-width" inputId="package-storage-default-place-width"></MeasurementInput>
      </div>

      <div class="field" :class="{ changed: defaultMaxStackCountChanged }">
        <label for="package-storage-default-max-stack-count">Default max stack count</label>
        <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="defaultMaxStackCount" id="package-storage-default-max-stack-count"></InputNumber>
      </div>

      <div class="field" :class="{ changed: defaultMaxWeightChanged }">
        <label for="package-storage-default-max-weight">Default max weight</label>
        <InputGroup>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="defaultMaxWeight" id="package-storage-default-max-weight"></InputNumber>
          <InputGroupAddon>t</InputGroupAddon>
        </InputGroup>
      </div>

      <div class="field" :class="{ changed: defaultMaxHeightChanged }">
        <label for="package-storage-default-max-height">Default max height</label>
        <MeasurementInput v-model:modelValue="defaultMaxHeight" unitKey="package-default-max-height" inputId="package-storage-default-max-height"></MeasurementInput>
      </div>

      <div class="field package-storage-meta">
        <label>{{ translations.generic.amount }}</label>
        <div>{{ places.length }} storage places</div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Generate places</div>
      <div class="package-storage-grid">
        <div class="field">
          <label for="package-storage-grid-columns">Columns</label>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="gridColumns" :min="1" id="package-storage-grid-columns"></InputNumber>
        </div>
        <div class="field">
          <label for="package-storage-grid-rows">Rows</label>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="gridRows" :min="1" id="package-storage-grid-rows"></InputNumber>
        </div>
        <div class="field">
          <label for="package-storage-h-gap">Horizontal gap</label>
          <MeasurementInput v-model:modelValue="hGap" unitKey="package-horizontal-gap" :min="0" inputId="package-storage-h-gap"></MeasurementInput>
        </div>
        <div class="field">
          <label for="package-storage-v-gap">Vertical gap</label>
          <MeasurementInput v-model:modelValue="vGap" unitKey="package-vertical-gap" :min="0" inputId="package-storage-v-gap"></MeasurementInput>
        </div>
        <div class="field package-storage-meta actions">
          <Button size="small" label="Generate grid" @click="generate"></Button>
          <Button size="small" severity="secondary" label="Apply horizontal gap" @click="applyHorizontalGap"></Button>
          <Button size="small" severity="secondary" label="Apply vertical gap" @click="applyVerticalGap"></Button>
        </div>
        <div class="field package-storage-meta gap-hint">
          {{ selectedPlaceIds.length > 0 ? `Applies to ${selectedPlaceIds.length} selected place(s)` : 'Applies to all places' }}
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title-row">
        <div class="section-title">Places</div>
        <div class="actions">
          <Button size="small" icon="pi pi-plus" label="Add" @click="addPlace"></Button>
          <Button size="small" severity="danger" icon="pi pi-trash" label="Remove" :disabled="selectedPlaceIds.length === 0" @click="removeSelected"></Button>
          <ColumnSelectorButton
            :columns="orderedPlaceColumns"
            :modelValue="selectedPlaceColumns"
            @update:modelValue="setSelectedPlaceColumns"
            @reorder="setPlaceColumnsOrder"
            @reset="resetPlaceColumns"
          ></ColumnSelectorButton>
          <Button v-if="authStore.isAdmin" size="small" severity="secondary" icon="pi pi-cog"
            :title="translations.systemSettings.page.storagearea.configurePlaceDisplayText"
            @click="showPlaceDisplayTextFormulaDialog = true"></Button>
        </div>
      </div>

      <DataTable
        :value="places"
        v-model:selection="tableSelection"
        dataKey="id"
        selectionMode="multiple"
        :metaKeySelection="false"
        scrollable
        scrollHeight="220px"
        size="small"
      >
        <Column selectionMode="multiple" headerStyle="width: 2.5rem"></Column>
        <Column v-for="col of selectedPlaceColumns" :key="col.field" :field="col.field">
          <template #header>
            <template v-if="col.kind === 'unit'">{{ col.unitLabel }} ({{ unitSymbol(col.unit!) }})</template>
            <template v-else>{{ col.tableHeader ?? col.header }}</template>
          </template>
          <template #body="{ data }">
            <template v-if="col.kind === 'unit'">{{ toDisplayUnit(data[col.field], col.unit!) }}</template>
            <template v-else-if="col.kind === 'displayText'">{{ placeDisplayTextPreview(data) }}</template>
            <template v-else-if="col.kind === 'access'">{{ directionSummary(data) }}</template>
            <template v-else-if="col.kind === 'disabled'">{{ data.disabled ? '✓' : '' }}</template>
            <template v-else>{{ data[col.field] }}</template>
          </template>
        </Column>
      </DataTable>
    </div>

    <div class="section" v-if="selectedPlaceIds.length > 0">
      <div class="section-title">
        {{ editMultiplePlaces ? `Edit ${selectedPlaceIds.length} places` : 'Edit place' }}
      </div>

      <div class="package-storage-grid" v-if="singlePlace">
        <div class="field">
          <label for="place-x">X</label>
          <MeasurementInput v-model:modelValue="placeX" unitKey="package-place-x" inputId="place-x"></MeasurementInput>
        </div>
        <div class="field">
          <label for="place-y">Y</label>
          <MeasurementInput v-model:modelValue="placeY" unitKey="package-place-y" inputId="place-y"></MeasurementInput>
        </div>
        <div class="field">
          <label for="place-width">Width</label>
          <MeasurementInput v-model:modelValue="placeWidth" unitKey="package-place-width" inputId="place-width"></MeasurementInput>
        </div>
        <div class="field">
          <label for="place-length">Length</label>
          <MeasurementInput v-model:modelValue="placeLength" unitKey="package-place-length" inputId="place-length"></MeasurementInput>
        </div>
        <div class="field">
          <label for="place-max-stack">Max stack count</label>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="placeMaxStackCount" id="place-max-stack"></InputNumber>
        </div>
        <div class="field">
          <label for="place-max-weight">Max weight</label>
          <InputGroup>
            <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="placeMaxWeight" id="place-max-weight"></InputNumber>
            <InputGroupAddon>t</InputGroupAddon>
          </InputGroup>
        </div>
        <div class="field">
          <label for="place-max-height">Max height</label>
          <MeasurementInput v-model:modelValue="placeMaxHeight" unitKey="package-place-max-height" inputId="place-max-height"></MeasurementInput>
        </div>
        <div class="field">
          <label for="place-z-index">Z-index</label>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="placeZIndex" :min="areaZIndex" :step="1" showButtons id="place-z-index"></InputNumber>
        </div>
        <div class="field">
          <label for="place-column">Column</label>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="placeColumn" :min="1" :step="1" id="place-column"></InputNumber>
        </div>
        <div class="field">
          <label for="place-row">Row</label>
          <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="placeRow" :min="1" :step="1" id="place-row"></InputNumber>
        </div>
        <div class="field">
          <label for="place-accessibility-score-decrement">Score decrement per blocker</label>
          <InputNumber
            pt:pcInputText:root:autocomplete="off"
            v-model:modelValue="placeAccessibilityScoreDecrementPerBlocker"
            :max="0"
            :step="1"
            id="place-accessibility-score-decrement"
          ></InputNumber>
        </div>
        <div class="field checkbox-field">
          <Checkbox v-model:modelValue="singleDisabled" :binary="true" inputId="place-disabled"></Checkbox>
          <label for="place-disabled">Disabled</label>
        </div>
        <div class="field package-storage-meta">
          <label for="place-display-text">{{ translations.systemSettings.page.storagearea.placeDisplayText }}</label>
          <InputText id="place-display-text" :modelValue="placeDisplayTextPreview(singlePlace)" readonly></InputText>
        </div>
      </div>

      <div class="batch-actions" v-else>
        <div class="package-storage-grid">
          <div class="field">
            <label for="batch-dx">Move X</label>
            <MeasurementInput v-model:modelValue="batchDx" unitKey="package-batch-dx" inputId="batch-dx"></MeasurementInput>
          </div>
          <div class="field">
            <label for="batch-dy">Move Y</label>
            <MeasurementInput v-model:modelValue="batchDy" unitKey="package-batch-dy" inputId="batch-dy"></MeasurementInput>
          </div>
          <div class="field package-storage-meta">
            <Button size="small" label="Move selected" @click="applyBatchMove"></Button>
          </div>
          <div class="field">
            <label for="batch-width">Set width</label>
            <MeasurementInput v-model:modelValue="batchWidth" unitKey="package-batch-width" :min="0" inputId="batch-width"></MeasurementInput>
          </div>
          <div class="field">
            <label for="batch-length">Set length</label>
            <MeasurementInput v-model:modelValue="batchLength" unitKey="package-batch-length" :min="0" inputId="batch-length"></MeasurementInput>
          </div>
          <div class="field package-storage-meta">
            <Button size="small" label="Resize selected" @click="applyBatchResize"></Button>
          </div>
          <div class="field">
            <label for="batch-z-index">Set z-index</label>
            <InputNumber pt:pcInputText:root:autocomplete="off" v-model:modelValue="batchZIndex" :min="areaZIndex" :step="1" showButtons id="batch-z-index"></InputNumber>
          </div>
          <div class="field package-storage-meta">
            <Button size="small" label="Apply z-index" :disabled="batchZIndex === null" @click="applyBatchZIndex"></Button>
          </div>
          <div class="field package-storage-meta actions">
            <Button size="small" severity="secondary" label="Enable all" @click="setSelectedDisabled(false)"></Button>
            <Button size="small" severity="secondary" label="Disable all" @click="setSelectedDisabled(true)"></Button>
          </div>
        </div>
      </div>

      <div class="directions">
        <label>Pickup access directions</label>
        <div class="direction-buttons">
          <Button
            v-for="d in directionLabels"
            :key="d.direction"
            size="small"
            :label="d.label"
            :severity="directionState(d.direction) === true ? 'success' : directionState(d.direction) === null ? 'warn' : 'secondary'"
            :outlined="directionState(d.direction) !== true"
            @click="toggleDirection(d.direction)"
          ></Button>
        </div>
      </div>
    </div>

    <StoragePlaceDisplayTextFormulaDialog v-model:visible="showPlaceDisplayTextFormulaDialog"></StoragePlaceDisplayTextFormulaDialog>
  </div>
</template>

<style scoped>
.package-storage {
  display: flex;
  flex-direction: column;
  gap: .75rem;
}

.package-storage-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.package-storage-meta {
  grid-column: 1 / 3;
}

.gap-hint {
  font-size: .8rem;
  color: var(--p-text-muted-color, #6b7280);
}

.section {
  border-top: 1px solid var(--p-content-border-color, #e5e7eb);
  padding-top: .5rem;
}

.section-title {
  font-weight: 600;
  margin-bottom: .5rem;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .5rem;
}

.actions {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: .5rem;
}

.batch-actions {
  display: flex;
  gap: .5rem;
  margin-bottom: .5rem;
}

.directions {
  margin-top: .5rem;
  display: flex;
  flex-direction: column;
  gap: .35rem;
}

.direction-buttons {
  display: flex;
  gap: .5rem;
}
</style>

